import { InteractionRequiredAuthError } from '@azure/msal-browser';
import UrlUtilities from '@shared/common/UrlUtilities';
import InovxLoader from '@shared/global/InovxLoader';
import { ensureSyncfusionLocaleLoaded } from '@shared/global/syncfusionLocale';
import { msalInstance } from './AuthProvider';
import { exchangeTokenRequest } from './msalConfig';
import { applyAuthBootstrapGlobals, clearAuthBootstrapGlobals } from './initGlobals';

export interface PlatformSessionUser {
    ID: string;
    UserName: string;
    DisplayName: string;
    FirstName?: string;
    LastName?: string;
    Email?: string;
    ExternalAccountUserName?: string;
}

export interface PlatformSessionProfile {
    UserName: string;
    FirstName?: string;
    LastName?: string;
    Email?: string;
    ExternalAccountUserName?: string;
    PasswordExpired?: boolean;
    AuthenticationMode?: string;
    ExternalProviderName?: string;
}

export interface PlatformSessionLocale {
    LanguageCode?: string;
    DateFormat?: string;
    TimeFormat?: string;
    TimeZoneName?: string;
}

export interface PlatformSessionBootstrap {
    Result: string;
    AuthMode?: 'local' | 'external' | string;
    AccessToken: string;
    ExpiresIn: number;
    TokenType: string;
    User: PlatformSessionUser;
    Permissions: string[];
    Applications: string[];
    Profile: PlatformSessionProfile;
    Locale: PlatformSessionLocale | null;
    AuthenticationMode: string;
    ExternalProviderName?: string;
    SingleSignOut: boolean;
}

export interface PlatformSessionRegistrationProfile {
    Username: string;
    Email: string;
    FirstName?: string;
    LastName?: string;
    ExternalProviderName?: string;
    EnablePasswordEntry: boolean;
}

export interface PlatformSessionRegistrationInput {
    email: string;
    firstName?: string;
    lastName?: string;
    newPassword?: string;
    confirmPassword?: string;
}

export interface PlatformSessionFailure {
    Result: string;
    Reason?: string;
    Message?: string;
    PropertyName?: string;
    AuthMode?: string;
    AuthenticationMode?: string;
    ExternalProviderName?: string;
    SingleSignOut?: boolean;
    Registration?: PlatformSessionRegistrationProfile;
}

export interface AuthSessionOptions {
    AuthenticationMode: string;
    EnableLocalLogin: boolean;
    EnableExternalLogin: boolean;
    ExternalProviderName?: string;
    SingleSignOut: boolean;
}

export interface LocalPlatformSessionInput {
    username: string;
    password: string;
}

export interface LocalPasswordChangeInput {
    username: string;
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface CachedPlatformSession {
    bootstrap: PlatformSessionBootstrap;
    expiresOn: Date;
}

const localSessionStorageKey = 'vplant.localPlatformSession';
const nativeFetch = window.fetch.bind(window);

let msalInitializedPromise: Promise<void> | null = null;
let sessionRefreshPromise: Promise<PlatformSessionBootstrap | null> | null = null;
let cachedPlatformSession: CachedPlatformSession | null = null;
let restoredSessionLocalePromise: Promise<void> | null = null;

export class PlatformSessionNoAccessError extends Error {
    readonly response: PlatformSessionFailure;

    constructor(response: PlatformSessionFailure) {
        super(response?.Message || InovxLoader.t('common:auth.accountNoAccess'));
        this.name = 'PlatformSessionNoAccessError';
        this.response = response;
    }
}

export class PlatformSessionRegistrationError extends Error {
    readonly response: PlatformSessionFailure;
    readonly status: number;

    constructor(response: PlatformSessionFailure, status: number) {
        super(response?.Message || InovxLoader.t('common:auth.registrationFailed'));
        this.name = 'PlatformSessionRegistrationError';
        this.response = response;
        this.status = status;
    }
}

export class PlatformSessionLocalAuthError extends Error {
    readonly response: PlatformSessionFailure;
    readonly status: number;

    constructor(response: PlatformSessionFailure, status: number) {
        super(response?.Message || InovxLoader.t('common:auth.localSignInFailed'));
        this.name = 'PlatformSessionLocalAuthError';
        this.response = response;
        this.status = status;
    }
}

export function getCurrentPlatformSession(): PlatformSessionBootstrap | null {
    restoreCachedPlatformSession();

    if (!isSessionActive(cachedPlatformSession)) {
        if (cachedPlatformSession) {
            clearPlatformSession();
        }

        return null;
    }

    return cachedPlatformSession.bootstrap;
}

export function getCurrentPlatformAccessToken(): string | null {
    return getCurrentPlatformSession()?.AccessToken ?? null;
}

export async function ensurePlatformAccessTokenReady(): Promise<string | null> {
    const session = await ensurePlatformSessionReady();
    return session?.AccessToken ?? null;
}

export function isSingleSignOutEnabled(): boolean {
    return getCurrentPlatformSession()?.SingleSignOut === true;
}

export function getCurrentPlatformAuthMode(): string | null {
    return getCurrentPlatformSession()?.AuthMode ?? null;
}

export function clearPlatformSession() {
    cachedPlatformSession = null;
    restoredSessionLocalePromise = null;
    removeStoredLocalSession();
    clearAuthBootstrapGlobals();
}

export async function ensurePlatformSessionReady(): Promise<PlatformSessionBootstrap | null> {
    restoreCachedPlatformSession();

    if (isSessionActive(cachedPlatformSession)) {
        await restoredSessionLocalePromise;
        return cachedPlatformSession.bootstrap;
    }

    return queuePlatformSessionRefresh();
}

function queuePlatformSessionRefresh(): Promise<PlatformSessionBootstrap | null> {
    if (!sessionRefreshPromise) {
        sessionRefreshPromise = refreshPlatformSession()
            .finally(() => {
                sessionRefreshPromise = null;
            });
    }

    return sessionRefreshPromise;
}

export async function registerExternalPlatformSession(input: PlatformSessionRegistrationInput): Promise<PlatformSessionBootstrap> {
    const entraAccessToken = await acquireExchangeToken();
    if (!entraAccessToken) {
        clearPlatformSession();
        throw new PlatformSessionRegistrationError({ Result: 'NoAccess', Message: InovxLoader.t('common:auth.externalTokenFailed') }, 401);
    }

    const response = await nativeFetch(getRegisterEndpointUrl(), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${entraAccessToken}`,
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            Email: input.email,
            FirstName: input.firstName || '',
            LastName: input.lastName || '',
            NewPassword: input.newPassword || '',
            ConfirmPassword: input.confirmPassword || '',
        }),
    });

    if (response.status === 403) {
        clearPlatformSession();
        const failure = await safeReadJson<PlatformSessionFailure>(response, { Result: 'NoAccess' });
        throw new PlatformSessionNoAccessError(failure);
    }

    if (!response.ok) {
        const failure = await safeReadJson<PlatformSessionFailure>(response, { Result: 'NoAccess' });
        throw new PlatformSessionRegistrationError(failure, response.status);
    }

    const bootstrap = await response.json() as PlatformSessionBootstrap;
    await cachePlatformSession(bootstrap);
    return bootstrap;
}

export async function getAuthSessionOptions(): Promise<AuthSessionOptions> {
    const response = await nativeFetch(getOptionsEndpointUrl(), {
        method: 'GET',
        headers: {
            Accept: 'application/json',
        },
    });

    if (!response.ok) {
        throw new Error(`Authentication options request failed with status ${response.status}.`);
    }

    return await response.json() as AuthSessionOptions;
}

export async function establishLocalPlatformSession(input: LocalPlatformSessionInput): Promise<PlatformSessionBootstrap> {
    return postLocalSession(getLocalEndpointUrl(), {
        Username: input.username,
        Password: input.password,
    });
}

export async function changeExpiredLocalPassword(input: LocalPasswordChangeInput): Promise<void> {
    await postLocalPasswordChange({
        Username: input.username,
        OldPassword: input.oldPassword,
        NewPassword: input.newPassword,
        ConfirmPassword: input.confirmPassword,
    });
}

async function refreshPlatformSession(): Promise<PlatformSessionBootstrap | null> {
    const entraAccessToken = await acquireExchangeToken();
    if (!entraAccessToken) {
        clearPlatformSession();
        return null;
    }

    const response = await nativeFetch(getExchangeEndpointUrl(), {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${entraAccessToken}`,
            Accept: 'application/json',
        },
    });

    if (response.status === 403) {
        clearPlatformSession();
        const failure = await safeReadJson<PlatformSessionFailure>(response, { Result: 'NoAccess' });
        throw new PlatformSessionNoAccessError(failure);
    }

    if (!response.ok) {
        clearPlatformSession();
        throw new Error(`Platform session exchange failed with status ${response.status}.`);
    }

    const bootstrap = await response.json() as PlatformSessionBootstrap;
    await cachePlatformSession(bootstrap);
    return bootstrap;
}

async function postLocalSession(endpointUrl: string, body: Record<string, string>): Promise<PlatformSessionBootstrap> {
    const response = await postLocalAuthRequest(endpointUrl, body);

    const bootstrap = await response.json() as PlatformSessionBootstrap;
    await cachePlatformSession(bootstrap);
    return bootstrap;
}

async function postLocalPasswordChange(body: Record<string, string>): Promise<void> {
    await postLocalAuthRequest(getLocalChangePasswordEndpointUrl(), body);
}

async function postLocalAuthRequest(endpointUrl: string, body: Record<string, string>): Promise<Response> {
    const response = await nativeFetch(endpointUrl, {
        method: 'POST',
        headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
    });

    if (!response.ok) {
        const failure = await safeReadJson<PlatformSessionFailure>(response, { Result: 'NoAccess' });
        throw new PlatformSessionLocalAuthError(failure, response.status);
    }

    return response;
}

async function cachePlatformSession(bootstrap: PlatformSessionBootstrap) {
    cachedPlatformSession = {
        bootstrap,
        expiresOn: new Date(Date.now() + Math.max(0, bootstrap.ExpiresIn) * 1000),
    };

    if (bootstrap.AuthMode === 'local') {
        storeLocalSession(bootstrap, cachedPlatformSession.expiresOn);
    } else {
        removeStoredLocalSession();
    }

    await applyPlatformSessionLocale(bootstrap.Locale);
    applyAuthBootstrapGlobals(bootstrap.Permissions || [], bootstrap.Applications || []);
}

function restoreCachedPlatformSession() {
    if (cachedPlatformSession) {
        return;
    }

    const serializedSession = getStoredLocalSession();
    if (!serializedSession) {
        return;
    }

    try {
        const restoredSession = JSON.parse(serializedSession) as CachedPlatformSession & { expiresOn: string };
        const parsedSession = {
            bootstrap: restoredSession.bootstrap,
            expiresOn: new Date(restoredSession.expiresOn),
        };

        if (!isSessionActive(parsedSession)) {
            removeStoredLocalSession();
            return;
        }

        cachedPlatformSession = parsedSession;
        applyAuthBootstrapGlobals(cachedPlatformSession.bootstrap.Permissions || [], cachedPlatformSession.bootstrap.Applications || []);
        restoredSessionLocalePromise = applyPlatformSessionLocale(cachedPlatformSession.bootstrap.Locale)
            .catch(() => undefined)
            .finally(() => {
                restoredSessionLocalePromise = null;
            });
    } catch {
        removeStoredLocalSession();
    }
}

function getStoredLocalSession(): string | null {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(localSessionStorageKey);
}

function storeLocalSession(bootstrap: PlatformSessionBootstrap, expiresOn: Date) {
    if (typeof sessionStorage === 'undefined') {
        return;
    }

    sessionStorage.setItem(localSessionStorageKey, JSON.stringify({
        bootstrap,
        expiresOn: expiresOn.toISOString(),
    }));
}

function removeStoredLocalSession() {
    if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem(localSessionStorageKey);
    }
}

async function applyPlatformSessionLocale(locale: PlatformSessionLocale | null) {
    if (!locale?.LanguageCode) {
        return;
    }

    const currentLocale = InovxLoader.getLocaleData();
    await InovxLoader.setLocaleData(
        locale.LanguageCode,
        locale.DateFormat || currentLocale.datePattern,
        locale.TimeFormat || currentLocale.timePattern,
        locale.TimeZoneName || currentLocale.tz
    );
    await ensureSyncfusionLocaleLoaded();
}

async function acquireExchangeToken(): Promise<string | null> {
    await ensureMsalInitialized();

    let account = msalInstance.getActiveAccount();
    if (!account) {
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            account = accounts[0];
            msalInstance.setActiveAccount(account);
        }
    }

    if (!account) {
        return null;
    }

    try {
        const response = await msalInstance.acquireTokenSilent({
            ...exchangeTokenRequest,
            account,
        });

        return response.accessToken;
    } catch (error) {
        if (error instanceof InteractionRequiredAuthError) {
            await msalInstance.acquireTokenRedirect({
                ...exchangeTokenRequest,
                account,
            });

            return null;
        }

        throw error;
    }
}

async function ensureMsalInitialized() {
    if (!msalInitializedPromise) {
        msalInitializedPromise = msalInstance.initialize();
    }

    await msalInitializedPromise;
}

async function safeReadJson<T>(response: Response, fallback: T): Promise<T> {
    try {
        return await response.json() as T;
    } catch {
        return fallback;
    }
}

function getExchangeEndpointUrl(): string {
    const apiRoot = (UrlUtilities.getApiUrl() || '').replace(/\/$/, '');
    return `${apiRoot}/api/auth/session/external/exchange`;
}

function getRegisterEndpointUrl(): string {
    const apiRoot = (UrlUtilities.getApiUrl() || '').replace(/\/$/, '');
    return `${apiRoot}/api/auth/session/external/register`;
}

function getOptionsEndpointUrl(): string {
    const apiRoot = (UrlUtilities.getApiUrl() || '').replace(/\/$/, '');
    return `${apiRoot}/api/auth/session/options`;
}

function getLocalEndpointUrl(): string {
    const apiRoot = (UrlUtilities.getApiUrl() || '').replace(/\/$/, '');
    return `${apiRoot}/api/auth/session/local`;
}

function getLocalChangePasswordEndpointUrl(): string {
    const apiRoot = (UrlUtilities.getApiUrl() || '').replace(/\/$/, '');
    return `${apiRoot}/api/auth/session/local/change-password`;
}

function isSessionActive(session: CachedPlatformSession | null): session is CachedPlatformSession {
    if (!session) {
        return false;
    }

    return session.expiresOn.getTime() - Date.now() > 30_000;
}
